import { ApiProblemError } from '../../app/api/slider-fetch';

export type ErrorStateKind = '401' | '404' | '403' | '500' | 'unexpected';

export function resolveErrorStateKind(error: unknown): ErrorStateKind {
  if (!(error instanceof ApiProblemError)) return 'unexpected';
  if (error.status === 401) return '401';
  if (error.status === 403) return '403';
  if (error.status === 404) return '404';
  if (error.status >= 500) return '500';
  return 'unexpected';
}

export function getErrorRequestId(error: unknown): string | undefined {
  return error instanceof ApiProblemError ? error.requestId : undefined;
}

type ErrorCopy = {
  title: string;
  description: string;
  primary: string;
  support: string;
  requestId: string;
};

const translations: Record<'en' | 'fa', Record<ErrorStateKind, ErrorCopy>> = {
  en: {
    '401': {
      title: 'Session expired',
      description: 'Your sign-in session has expired. Sign in again to continue securely.',
      primary: 'Sign in again',
      support: 'Contact support',
      requestId: 'Request ID',
    },
    '404': {
      title: 'Page not found',
      description: 'The page you were looking for does not exist or may have moved.',
      primary: 'Return home',
      support: 'Contact support',
      requestId: 'Request ID',
    },
    '403': {
      title: 'Access denied',
      description: 'You do not have permission to view this page. Contact your team administrator if you need access.',
      primary: 'Go to dashboard',
      support: 'Contact support',
      requestId: 'Request ID',
    },
    '500': {
      title: 'Internal server error',
      description: 'There was a problem on our server. Please try again in a moment.',
      primary: 'Try again',
      support: 'Contact support',
      requestId: 'Request ID',
    },
    unexpected: {
      title: 'Something went wrong',
      description: 'We could not complete this action. Try again or contact support if the problem continues.',
      primary: 'Try again',
      support: 'Contact support',
      requestId: 'Request ID',
    },
  },
  fa: {
    '401': {
      title: 'نشست شما پایان یافته است',
      description: 'اعتبار نشست شما به پایان رسیده است. برای ادامهٔ امن، دوباره وارد شوید.',
      primary: 'ورود مجدد',
      support: 'تماس با پشتیبانی',
      requestId: 'شناسه درخواست',
    },
    '404': {
      title: 'صفحه پیدا نشد',
      description: 'به نظر می‌رسد صفحه‌ای که به دنبال آن هستید وجود ندارد یا ممکن است جابه‌جا شده باشد.',
      primary: 'بازگشت به خانه',
      support: 'تماس با پشتیبانی',
      requestId: 'شناسه درخواست',
    },
    '403': {
      title: 'دسترسی ندارید',
      description: 'شما مجوز مشاهده این صفحه را ندارید. در صورت نیاز به دسترسی، با مدیر تیم خود تماس بگیرید.',
      primary: 'رفتن به داشبورد',
      support: 'تماس با پشتیبانی',
      requestId: 'شناسه درخواست',
    },
    '500': {
      title: 'خطای داخلی سرور',
      description: 'مشکلی در سرور رخ داده است. لطفاً چند لحظه بعد دوباره تلاش کنید.',
      primary: 'تلاش دوباره',
      support: 'تماس با پشتیبانی',
      requestId: 'شناسه درخواست',
    },
    unexpected: {
      title: 'مشکلی پیش آمد',
      description: 'در انجام این عملیات خطایی رخ داده است. لطفاً دوباره تلاش کنید یا در صورت ادامه مشکل، با پشتیبانی تماس بگیرید.',
      primary: 'تلاش دوباره',
      support: 'تماس با پشتیبانی',
      requestId: 'شناسه درخواست',
    },
  },
};

export function getErrorCopy(kind: ErrorStateKind, language: string): ErrorCopy {
  return translations[language.startsWith('fa') ? 'fa' : 'en'][kind];
}